import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, KeyRound, Loader2, Copy, Check, Pencil, X, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import { useOrgScope } from '../../stores/orgScope';
import { ConfirmModal } from '../../components/atoms/Modal';

interface Org {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

interface AdminUser {
  id: string;
  username: string;
  role: string;
  orgId: string | null;
  createdAt: string;
}

export function AdminDashboard() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      const res = await apiFetch('/api/admin/orgs');
      const data = await res.json().catch(() => ({})) as {
        orgs?: Org[];
        error?: string;
      };
      if (!res.ok) {
        setOrgs([]);
        setOrgsError(data.error ?? `Failed to load organizations (${res.status}).`);
        return;
      }
      setOrgs(data.orgs ?? []);
    } catch {
      setOrgs([]);
      setOrgsError('Could not reach the server while loading organizations.');
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="space-y-8">
        {/* Managers are governed in Org Settings → Traders as of #295. This page
            keeps only the box-scoped surfaces: organizations and admin accounts. */}
        <OrgsPanel orgs={orgs} setOrgs={setOrgs} loading={orgsLoading} error={orgsError} />
        <AdminsPanel orgs={orgs} />
      </div>
    </div>
  );
}

// ── Orgs Panel ──────────────────────────────────────────────────

function OrgsPanel({
  orgs,
  setOrgs,
  loading,
  error: orgsError,
}: {
  orgs: Org[];
  setOrgs: React.Dispatch<React.SetStateAction<Org[]>>;
  loading: boolean;
  error: string | null;
}) {
  const navigate = useNavigate();
  const setSelectedOrgId = useOrgScope((s) => s.setSelectedOrgId);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Org | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    setSubmitting(true); setError(null);
    const res = await apiFetch('/api/admin/orgs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim(), slug: slug.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); setSubmitting(false); return; }
    setOrgs((prev) => [...prev, data.org]);
    setName(''); setSlug(''); setShowForm(false); setSubmitting(false);
  };

  const handleEdit = async (id: string) => {
    setError(null);
    const res = await apiFetch(`/api/admin/orgs/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editName.trim() || undefined, slug: editSlug.trim() || undefined }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setOrgs((prev) => prev.map((o) => (o.id === id ? { ...o, ...data.org } : o)));
    setEditingId(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await apiFetch(`/api/admin/orgs/${deleteTarget.id}`, { method: 'DELETE' });
    if (res.ok) setOrgs((prev) => prev.filter((o) => o.id !== deleteTarget.id));
    else { const data = await res.json(); setError(data.error); }
    setDeleteTarget(null);
  };

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Organizations</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> Create Org
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
                placeholder="Acme Trading" />
            </div>
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">Slug</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)}
                className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
                placeholder="acme-trading" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {submitting && <Loader2 size={12} className="animate-spin" />} Create
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      {orgsError && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{orgsError}</div>}
      {error && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="text-muted-foreground h-5 w-5 animate-spin" /></div>
      ) : orgs.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">No organizations yet.</p>
      ) : (
        <div className="bg-card border-border rounded-lg border">
          <table className="w-full table-fixed text-xs">
            <thead><tr className="text-muted-foreground border-border border-b">
              <th className="w-[30%] px-4 py-2.5 text-left font-medium">Name</th>
              <th className="w-[30%] px-4 py-2.5 text-left font-medium">Slug</th>
              <th className="w-[20%] px-4 py-2.5 text-left font-medium">Created</th>
              <th className="w-[20%] px-4 py-2.5 text-right font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.id} className="border-border/50 last:border-0 border-b">
                  {editingId === org.id ? (
                    <>
                      <td className="px-4 py-2">
                        <input value={editName} onChange={(e) => setEditName(e.target.value)}
                          className="bg-card border-border text-foreground w-full rounded border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" />
                      </td>
                      <td className="px-4 py-2">
                        <input value={editSlug} onChange={(e) => setEditSlug(e.target.value)}
                          className="bg-card border-border text-foreground w-full rounded border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none" />
                      </td>
                      <td className="text-muted-foreground px-4 py-2.5">{new Date(org.createdAt).toLocaleDateString()}</td>
                      <td className="space-x-1 px-4 py-2.5 text-right">
                        <button onClick={() => handleEdit(org.id)} className="rounded p-1 text-green-500 hover:bg-green-500/10"><Check size={14} /></button>
                        <button onClick={() => setEditingId(null)} className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><X size={14} /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-foreground px-4 py-2.5">{org.name}</td>
                      <td className="text-muted-foreground px-4 py-2.5 font-mono">{org.slug}</td>
                      <td className="text-muted-foreground px-4 py-2.5">{new Date(org.createdAt).toLocaleDateString()}</td>
                      <td className="space-x-1 px-4 py-2.5 text-right">
                        {/* Admins are org-floating (no own org), so they reach
                            the unfiltered Org-Account failsafe per-org from
                            here. Managers get it as a sidebar nav item for
                            their own org. */}
                        <button onClick={() => { setSelectedOrgId(org.id); navigate('/organization/org-account', { state: { from: 'administration' } }); }} title="View Org Account (balance + all positions/orders)"
                          className="text-muted-foreground rounded p-1 hover:bg-primary/10 hover:text-primary"><Building2 size={14} /></button>
                        <button onClick={() => { setEditingId(org.id); setEditName(org.name); setEditSlug(org.slug); }} title="Edit"
                          className="text-muted-foreground rounded p-1 hover:bg-blue-500/10 hover:text-blue-400"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteTarget(org)} title="Delete"
                          className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={14} /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Organization"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </section>
  );
}

// ── Admins Panel ────────────────────────────────────────────────

function AdminsPanel({ orgs }: { orgs: Org[] }) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editOrgId, setEditOrgId] = useState<string>('');
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  const fetchAdmins = useCallback(async () => {
    const res = await apiFetch('/api/admin/admins');
    const data = await res.json();
    setAdmins(data.admins ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    setSubmitting(true); setError(null); setGeneratedPassword(null);
    const res = await apiFetch('/api/admin/admins', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); setSubmitting(false); return; }
    setAdmins((prev) => [...prev, { ...data.user, orgId: null }]);
    setGeneratedPassword(data.generatedPassword);
    setUsername(''); setShowForm(false); setSubmitting(false);
  };

  const handleEdit = async (id: string) => {
    setError(null);
    const res = await apiFetch(`/api/admin/admins/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: editUsername.trim() || undefined,
        orgId: editOrgId || null,
      }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setAdmins((prev) => prev.map((a) => (a.id === id ? { ...a, ...data.user } : a)));
    setEditingId(null);
    // If an admin just changed their OWN org, the access token + cached user
    // are now stale (the JWT still encodes the old org until refresh). Re-issue
    // the token and re-sync the user object so every subsequent request — the
    // Org Account view, trading-key selection, role surfaces — uses the new org
    // immediately instead of waiting up to 12h for the refresh timer.
    const me = useAuthStore.getState().user;
    if (me && me.id === id && (data.user?.orgId ?? null) !== (me.orgId ?? null)) {
      const ok = await useAuthStore.getState().refresh();
      if (ok) {
        await useAuthStore.getState().refreshMe();
      } else {
        // refresh() failed (a 5xx, or a rotated/invalidated refresh token from
        // another tab) and has already logged us out. Surface it instead of a
        // silent bounce to the login screen — the org change itself committed.
        setError('Org updated, but your session could not be re-synced — please sign in again.');
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await apiFetch(`/api/admin/admins/${deleteTarget.id}`, { method: 'DELETE' });
    if (res.ok) setAdmins((prev) => prev.filter((a) => a.id !== deleteTarget.id));
    else { const data = await res.json(); setError(data.error); }
    setDeleteTarget(null);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setGeneratedPassword(null);
    const res = await apiFetch(`/api/admin/admins/${resetTarget.id}/reset-password`, { method: 'POST' });
    const data = await res.json();
    if (res.ok) setGeneratedPassword(data.generatedPassword);
    else setError(data.error);
    setResetTarget(null);
  };

  const orgNameMap = new Map(orgs.map((o) => [o.id, o.name]));

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Admin Users</h2>
        <button onClick={() => { setShowForm(!showForm); setGeneratedPassword(null); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> Create Admin
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="newadmin" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {submitting && <Loader2 size={12} className="animate-spin" />} Create
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      <PasswordBanner password={generatedPassword} onDismiss={() => setGeneratedPassword(null)} />
      {error && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="text-muted-foreground h-5 w-5 animate-spin" /></div>
      ) : admins.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">None yet.</p>
      ) : (
        <div className="bg-card border-border rounded-lg border">
          <table className="w-full table-fixed text-xs">
            <thead><tr className="text-muted-foreground border-border border-b">
              <th className="w-[30%] px-4 py-2.5 text-left font-medium">Username</th>
              <th className="w-[30%] px-4 py-2.5 text-left font-medium">Org</th>
              <th className="w-[20%] px-4 py-2.5 text-left font-medium">Created</th>
              <th className="w-[20%] px-4 py-2.5 text-right font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-border/50 last:border-0 border-b">
                  {editingId === a.id ? (
                    <>
                      <td className="px-4 py-2">
                        <input value={editUsername} onChange={(e) => setEditUsername(e.target.value)}
                          className="bg-card border-border text-foreground w-full rounded border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" />
                      </td>
                      <td className="px-4 py-2">
                        <select value={editOrgId} onChange={(e) => setEditOrgId(e.target.value)}
                          className="bg-card border-border text-foreground w-full rounded border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
                          <option value="">No org</option>
                          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      </td>
                      <td className="text-muted-foreground px-4 py-2.5">{new Date(a.createdAt).toLocaleDateString()}</td>
                      <td className="space-x-1 px-4 py-2.5 text-right">
                        <button onClick={() => handleEdit(a.id)} className="rounded p-1 text-green-500 hover:bg-green-500/10"><Check size={14} /></button>
                        <button onClick={() => setEditingId(null)} className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><X size={14} /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-foreground px-4 py-2.5">{a.username}</td>
                      <td className="text-muted-foreground px-4 py-2.5">{a.orgId ? (orgNameMap.get(a.orgId) ?? '—') : <span className="opacity-50">None</span>}</td>
                      <td className="text-muted-foreground px-4 py-2.5">{new Date(a.createdAt).toLocaleDateString()}</td>
                      <td className="space-x-1 px-4 py-2.5 text-right">
                        <button onClick={() => { setEditingId(a.id); setEditUsername(a.username); setEditOrgId(a.orgId ?? ''); }} title="Edit"
                          className="text-muted-foreground rounded p-1 hover:bg-blue-500/10 hover:text-blue-400"><Pencil size={14} /></button>
                        <button onClick={() => setResetTarget(a)} title="Reset password"
                          className="text-muted-foreground rounded p-1 hover:bg-yellow-500/10 hover:text-yellow-400"><KeyRound size={14} /></button>
                        <button onClick={() => setDeleteTarget(a)} title="Delete"
                          className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={14} /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Generate a new password for "${resetTarget?.username}"? The current password will be invalidated immediately.`}
        confirmLabel="Reset Password"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Admin"
        message={`Are you sure you want to delete admin "${deleteTarget?.username}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </section>
  );
}

// ── Shared Components ───────────────────────────────────────────

function PasswordBanner({
  password,
  onDismiss,
}: {
  password: string | null;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!password) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 rounded-lg border border-green-700/50 bg-green-900/20 p-4 dark:border-green-800">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-green-600 dark:text-green-400">Generated Password</span>
        <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
      </div>
      <div className="flex items-center gap-2">
        <code className="bg-card border-border flex-1 rounded border px-3 py-1.5 font-mono text-sm text-green-700 dark:text-green-300">
          {password}
        </code>
        <button onClick={handleCopy} title="Copy" className="text-muted-foreground hover:text-foreground hover:bg-muted rounded p-1.5">
          {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
      </div>
      <p className="text-muted-foreground mt-2 text-xs">Save this password now. It will not be shown again.</p>
    </div>
  );
}
